import { useCallback, useEffect, useRef } from "react";
import { useLang } from "../hooks/useLang";
import type { PaymentProvider } from "../services/api";

// PaymentMethodModal — "Öde" tıklandığında açılan yöntem seçimi.
//
// Neden ayrı bir adım: iki yöntem farklı PARA BİRİMİ tahsil ediyor (Garanti TL,
// PayPal USD). Kullanıcı hangi tutarın çekileceğini seçimden ÖNCE görmeli; bu
// yüzden her kart kendi tutarını yazar, seçim kartın üstünde yapılır.
//
// Popup engelleyici notu: seçim tıklaması bir kullanıcı jestidir, dolayısıyla
// onPick içinde window.open SENKRON çağrılabilir. Modal açılışı bu jesti
// tüketmez — çağıran taraf popup'ı onPick'te açmalıdır (bkz. CreditsPanel).

export type PaymentMethodModalProps = {
  open: boolean;
  /** Backend'in açık bildirdiği yöntemler (/api/v1/me → payment_providers). */
  methods: PaymentProvider[];
  /** Satın alınacak kredi adedi — tutarlar bundan hesaplanır. */
  credits: number;
  /** Kredi başına USD (admin ayarı). */
  unitUSD: number;
  /** USD/TRY kuru; 0 ise Garanti seçeneği tutar gösteremez ve kapalıdır. */
  usdTryRate: number;
  /** Ödeme başlatılan yöntem (o kart "başlatılıyor" gösterir, diğerleri kilitlenir). */
  busy: PaymentProvider | null;
  onPick: (provider: PaymentProvider) => void;
  onClose: () => void;
};

const LOGO: Record<PaymentProvider, string> = {
  garanti: "/pay/garanti.svg",
  paypal: "/pay/paypal.svg",
};

export default function PaymentMethodModal({
  open, methods, credits, unitUSD, usdTryRate, busy, onPick, onClose,
}: PaymentMethodModalProps) {
  const { t } = useLang();
  const boxRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);
  // Modal kapanınca odak, açan butona geri dönmeli (klavye kullanıcısı listenin
  // başına savrulmasın) — açılışta odağın nerede olduğunu saklıyoruz.
  const returnFocusRef = useRef<Element | null>(null);

  const close = useCallback(() => {
    if (busy) return; // ödeme başlatılırken kapatma — yarım kalmış izlenimi verir
    onClose();
  }, [busy, onClose]);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement;
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); close(); return; }
      if (e.key !== "Tab") return;
      // Basit odak tuzağı: Tab kutunun dışına çıkmasın.
      const items = boxRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (!items || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      (returnFocusRef.current as HTMLElement | null)?.focus?.();
    };
  }, [open, close]);

  if (!open) return null;

  // Tutarlar BACKEND ile aynı formülden: yuvarlama en sonda, bir kez.
  // Garanti  → round(unitUSD × credits × rate, 2) TL   (payment.go checkoutGaranti)
  // PayPal   → round(unitUSD × credits, 2) USD          (payment.go checkoutPayPal)
  const totalTL = Math.round(unitUSD * usdTryRate * credits * 100) / 100;
  const totalUSD = Math.round(unitUSD * credits * 100) / 100;

  const info = (p: PaymentProvider) => p === "paypal"
    ? { name: "PayPal", amount: `${totalUSD.toFixed(2)} USD`, note: t("pm_paypal_note"), ready: unitUSD > 0 }
    : { name: t("pm_garanti_name"), amount: `${totalTL.toFixed(2)} TL`, note: t("pm_garanti_note"), ready: usdTryRate > 0 && unitUSD > 0 };

  return (
    <div
      className="pm-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="pm-modal" ref={boxRef}
        role="dialog" aria-modal="true" aria-labelledby="pm-title"
      >
        <h3 id="pm-title" className="pm-title">{t("pm_title")}</h3>
        <p className="pm-sub">{t("pm_sub").replace("{n}", String(credits))}</p>

        <div className="pm-options">
          {methods.map((p, i) => {
            const m = info(p);
            const isBusy = busy === p;
            const disabled = !m.ready || (busy !== null && !isBusy);
            return (
              <button
                key={p}
                ref={i === 0 ? firstRef : undefined}
                type="button"
                className="pm-option"
                disabled={disabled}
                onClick={() => onPick(p)}
              >
                <img className="pm-logo" src={LOGO[p]} alt="" width={40} height={40} aria-hidden="true" />
                <span className="pm-body">
                  <span className="pm-name">{m.name}</span>
                  <span className="pm-note">{m.ready ? m.note : t("cr_method_unavailable")}</span>
                </span>
                <span className="pm-amount">{isBusy ? t("cr_starting") : m.amount}</span>
              </button>
            );
          })}
        </div>

        <p className="pm-secure">{t("pm_secure")}</p>
        <button type="button" className="btn-sec pm-cancel" onClick={close} disabled={busy !== null}>
          {t("pm_cancel")}
        </button>
      </div>
    </div>
  );
}
